/**
 * useLanguage — app-level language management hook.
 *
 * Zero hardcoded language codes.
 * Supported languages come from the language registry in i18n.ts,
 * which is populated from languages.json (CDN or bundled).
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import dayjs from 'dayjs';
import {
  applyDocumentDir,
  persistLang,
  getAvailableLanguages,
  getLanguageDef,
  LanguageDefinition,
} from '../i18n/i18n';
import { userService } from '../services/user';
import { getToken } from '../utility/tokenManager';

// ─── Expose LanguageDefinition for consumers ─────────────────────────────────
export type { LanguageDefinition };

/**
 * useLanguage
 *
 * @example
 *   const { t, currentLang, currentLangDef, availableLanguages, changeLang } = useLanguage();
 *
 *   <h1>{t('auth.login.title')}</h1>
 *
 *   // Switch to ANY language — works for any code in languages.json, not just en/ar
 *   <button onClick={() => changeLang('fr')}>Français</button>
 *   <button onClick={() => changeLang('zh')}>中文</button>
 *
 *   // Build dropdown from registry — zero hardcoding
 *   {availableLanguages.map(lang => (
 *     <option key={lang.code} value={lang.code}>{lang.name}</option>
 *   ))}
 */
export function useLanguage() {
  const { t, i18n } = useTranslation();

  const currentLang: string = i18n.language ?? 'en';
  const currentLangDef: LanguageDefinition | undefined = getLanguageDef(currentLang);
  const availableLanguages: LanguageDefinition[] = getAvailableLanguages();

  const changeLang = useCallback(
    async (code: string) => {
      if (code === currentLang) return;
      await i18n.changeLanguage(code);
      applyDocumentDir(code);
      persistLang(code);
      // Sync moment.js's and dayjs's locale too, so every date formatted
      // app-wide (invoices, billing cards, tables, session modals, ...) shows
      // month names in the selected language instead of always defaulting to
      // English — different components use one library or the other.
      moment.locale(code);
      dayjs.locale(code);

      // Persist to the backend so emails/PDFs render in this language.
      // Best-effort: only when authenticated, and never block the UI on it.
      if (getToken('accessToken')) {
        void userService.setPreferredLanguage(code).catch(() => {
          /* non-fatal: header still carries lang for live requests */
        });
      }
    },
    [currentLang, i18n],
  );

  return {
    t,
    i18n,
    currentLang,
    currentLangDef,
    availableLanguages,
    changeLang,
    isRTL: currentLangDef?.dir === 'rtl',
  };
}
